import { memo, useState } from "react";

import { InviteMember } from "../InviteMember";

import { MemberDetailSheet } from "./MemberDetailSheet";
import WorkspaceFilter from "./WorkspaceFilter";
import {
  useOrganizationMembersData,
  useOrganizationMembersColumns,
  useOrganizationMembersActions,
  NormalizedMemberData,
  isOrganizationMember,
} from "./hooks";

import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { DataTable } from "@/components/ui/ag-grid-table";
import { TableColumn } from "@/components/ui/TableV2";
import { useUser } from "@/context/user-context";
import { useIsOrgOwnerById } from "@/hooks/organizations";

interface OrganizationMembersProps {
  organizationId?: number;
}

const OrganizationMembers = memo(({ organizationId }: OrganizationMembersProps = {}) => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const userContext = useUser();
  const {
    datasource,
    setGridApiRef,
    currentPage,
    pageSize,
    handlePaginationChanged,
    search,
    setSearch,
    workspaceIds,
    setWorkspaceIds,
    isLoading,
    refreshGrid,
  } = useOrganizationMembersData(organizationId);

  const [isInviteMemberDialogOpen, setIsInviteMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<NormalizedMemberData | null>(null);
  const [isMemberDetailOpen, setIsMemberDetailOpen] = useState(false);
  const { columns } = useOrganizationMembersColumns();
  const { actions, contextMenuItems, handlers } = useOrganizationMembersActions(
    organizationId,
    refreshGrid,
    refreshGrid,
  );
  const isOrgOwner = useIsOrgOwnerById(organizationId ?? 0);

  const handleRowClick = (member?: NormalizedMemberData) => {
    if (member) {
      setSelectedMember(member);
      setIsMemberDetailOpen(true);
    }
  };

  const getMemberKey = (m: NormalizedMemberData) =>
    isOrganizationMember(m) ? `user-${m.user.id}` : `invite-${m.invitee_email}`;

  const handleDelete = (item?: NormalizedMemberData) => {
    if (!item) {
      return;
    }
    const wasSelected = selectedMember && getMemberKey(selectedMember) === getMemberKey(item);
    handlers.handleDeleteMember(item);
    if (wasSelected) {
      setSelectedMember(null);
      setIsMemberDetailOpen(false);
    }
  };

  return (
    <>
      <DataTable<NormalizedMemberData>
        onRowClick={handleRowClick}
        data={[]}
        columns={columns as TableColumn<NormalizedMemberData>[]}
        searchPlaceholder="Search members by email"
        action={
          <InviteMember
            open={isInviteMemberDialogOpen}
            organizationId={organizationId || 0}
            setOpen={setIsInviteMemberDialogOpen}
            onInviteCreate={refreshGrid}
          />
        }
        searchBarAddon={
          <WorkspaceFilter
            organizationId={organizationId}
            selectedWorkspaceIds={workspaceIds}
            onChange={setWorkspaceIds}
          />
        }
        search={search}
        setSearch={setSearch}
        hasSearch
        rowsDeletable
        getDeleteDisabled={item => {
          if (!item) {
            return false;
          }
          const isSelf = isOrganizationMember(item) && item.user.id === userContext?.user?.id;
          return !isOrgOwner && !isSelf;
        }}
        getDeleteTooltip={item => {
          if (!item) {
            return undefined;
          }
          const isSelf = isOrganizationMember(item) && item.user.id === userContext?.user?.id;
          return !isOrgOwner && !isSelf ? "Only owners can delete members" : undefined;
        }}
        objectName="member"
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        onDelete={handleDelete}
        isHighlighted={row => {
          if (!row) {
            return false;
          }
          return isOrganizationMember(row) && row.user.id === userContext?.user?.id;
        }}
        actions={actions}
        contextMenuItems={contextMenuItems}
        emptyStateMessage="No members found"
        isLoading={isLoading}
        enablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        onPaginationChanged={handlePaginationChanged}
        rowModelType="infinite"
        datasource={datasource}
        cacheBlockSize={pageSize}
        maxConcurrentDatasourceRequests={1}
        infiniteInitialRowCount={pageSize}
        getApiRef={setGridApiRef}
      />
      <MemberDetailSheet
        open={isMemberDetailOpen}
        onOpenChange={setIsMemberDetailOpen}
        member={selectedMember}
        organizationId={organizationId ?? 0}
        onMemberDelete={refreshGrid}
        onInviteDelete={refreshGrid}
        onInviteCreate={refreshGrid}
      />
    </>
  );
});

OrganizationMembers.displayName = "OrganizationMembers";

export default OrganizationMembers;
